import { Injectable } from '@angular/core';
import { IBorrower } from '@app/models/borrower.model';
import { eProductNames } from '@app/models/fi-products.model';
import { LoanType } from '@app/models/Loan-Type';
import { IProposal } from '@app/models/proposal.model';
import { IVehicle } from '@app/models/vehicle.model';
import {
  defaultCollateralType,
  defaultOtherCollateralType
} from '@app/shared/constants/globalConstants';
import { loanProducts } from '@app/shared/constants/loanProducts';
import * as _ from 'lodash';
import * as moment from 'moment';

@Injectable({ providedIn: 'root' })
export class HubDataService {
  hubData = {
    LosId: {
      parentGroup: 'losInterfaceLink',
      keyName: 'losId'
    },
    AcctNum: {
      parentGroup: 'borrower',
      keyName: 'accountNumber'
    },
    FirstName: {
      parentGroup: 'borrower',
      keyName: 'firstName'
    },
    LastName: {
      parentGroup: 'borrower',
      keyName: 'lastName'
    },
    Address1: {
      parentGroup: 'borrower',
      keyName: 'address1'
    },
    Address2: {
      parentGroup: 'borrower',
      keyName: 'address2'
    },
    City: {
      parentGroup: 'borrower',
      keyName: 'city'
    },
    State: {
      parentGroup: 'borrower',
      keyName: 'state'
    },
    Zip: {
      parentGroup: 'borrower',
      keyName: 'zipCode'
    },
    Phone: {
      parentGroup: 'borrower',
      keyName: 'phoneNumber'
    },
    EmailAddress: {
      parentGroup: 'borrower',
      keyName: 'emailAddress'
    },
    DateOfBirth: {
      parentGroup: 'borrower',
      keyName: 'dateOfBirth'
    },
    COBFirstName: {
      parentGroup: 'coBorrower',
      keyName: 'firstName'
    },
    COBLastName: {
      parentGroup: 'coBorrower',
      keyName: 'lastName'
    },
    CoBorrowerDOB: {
      parentGroup: 'coBorrower',
      keyName: 'dateOfBirth'
    },
    BorrowerDLState: {
      parentGroup: 'losInterfaceLink',
      keyName: 'dlState'
    },
    BorrowerDLNumber: {
      parentGroup: 'losInterfaceLink',
      keyName: 'dlNumber'
    },
    VIN: {
      parentGroup: 'vehicle',
      keyName: 'vin'
    },
    MockVin: {
      parentGroup: 'vehicle',
      keyName: 'mockVin'
    },
    VehicleYear: {
      parentGroup: 'vehicle',
      keyName: 'year'
    },
    VehicleMake: {
      parentGroup: 'vehicle',
      keyName: 'make'
    },
    VehicleModel: {
      parentGroup: 'vehicle',
      keyName: 'model'
    },
    VehicleStyle: {
      parentGroup: 'vehicle',
      keyName: 'style'
    },
    collateralType: {
      parentGroup: 'vehicle',
      keyName: 'collateralType'
    },
    collateralCode: {
      parentGroup: 'vehicle',
      keyName: 'collateralCode'
    },
    IsVehicleNotFound: {
      parentGroup: 'vehicle',
      keyName: 'isVehicleNotFound'
    },
    Odometer: {
      parentGroup: 'vehicle',
      keyName: 'odometer'
    },
    VehiclePurchasePrice: {
      parentGroup: 'vehicle',
      keyName: 'purchasePrice'
    },
    RetailPrice: {
      parentGroup: 'vehicle',
      keyName: 'retailPrice'
    },
    ValidateVin: {
      parentGroup: 'vehicle',
      keyName: 'validateVin'
    },
    PurchasePrice: {
      parentGroup: 'loanData',
      keyName: 'totalAmountFinanced'
    },
    DownPayment: {
      parentGroup: 'loanData',
      keyName: 'deductions'
    },
    OtherFees: {
      parentGroup: 'loanData',
      keyName: 'otherFees'
    },
    PaymentsPerYear: {
      parentGroup: 'loanData',
      keyName: 'paymentsPerYear'
    },
    LoanDate: {
      parentGroup: 'loanData',
      keyName: 'loanDate'
    },
    LoanNum: {
      parentGroup: 'loanData',
      keyName: 'loanNumber'
    },
    ReferralCode: {
      parentGroup: 'loanData',
      keyName: 'referralCode'
    },
    LoanType: {
      parentGroup: 'loanData',
      keyName: 'loanType'
    },
    FirstPaymentDate: {
      parentGroup: 'loanData',
      keyName: 'firstPaymentDate'
    },
    SecondPaymentDate: {
      parentGroup: 'loanData',
      keyName: 'secondPaymentDate'
    },
    Rate: {
      parentGroup: 'loanData',
      keyName: 'interestRate'
    },
    Term: {
      parentGroup: 'loanData',
      keyName: 'termInMonths'
    },
    InsName: {
      parentGroup: 'loanData',
      keyName: 'insName'
    },
    InsPolicyNumber: {
      parentGroup: 'loanData',
      keyName: 'insPolicyNumber'
    },
    InsAgent: {
      parentGroup: 'loanData',
      keyName: 'insAgent'
    },
    InsAgentPhone: {
      parentGroup: 'loanData',
      keyName: 'insAgentPhone'
    },
    CombinationID: {
      parentGroup: 'losInterfaceLink',
      keyName: 'combinationId'
    },
    VIC: {
      parentGroup: 'losInterfaceLink',
      keyName: 'vic'
    },
    LoanToValue: {
      parentGroup: 'loanData',
      keyName: 'loanToValue'
    }
  };

  dateFieldsInProposal = [
    'secondPaymentDate',
    'firstPaymentDate',
    'loanDate',
    'dateOfBirth',
    'coverageDate'
  ];
  stringFieldsInProposal = [
    'zipCode',
    'phoneNumber',
    'accountNumber',
    'loanNumber',
    'address2',
    'losId'
  ];
  constructor() {}

  /**
   * method to parse a value
   * @param val any
   * @returns string | number
   */
  parseValue(val: any): string | number {
    return !isNaN(parseFloat(val)) && !isNaN(Number(val))
      ? Number(val)
      : val || '';
  }

  /**
   * method to generate a proposal object from hub data
   * @returns IProposal
   */
  public getProposalFromHubData(): IProposal {
    const hubData = JSON.parse(sessionStorage.getItem('hubData'));
    return _.chain(hubData)
      .reduce((init, val, key: string) => {
        const hubEntry = this.hubData[key];

        if (hubEntry) {
          const parsedValue = this.parseValue(val);
          _.set(init, [hubEntry.parentGroup, hubEntry.keyName], parsedValue);
        }

        this.processKeys(init, val, key);

        if (
          _.includes(this.stringFieldsInProposal, this.hubData[key]?.keyName)
        ) {
          const { parentGroup, keyName } = this.hubData[key];
          init[parentGroup][keyName] = val?.toString() || '';
        }

        this.checkDateFields(init, val, key, hubData);

        if (
          key === 'OtherCollateralVehicle' &&
          this.hubData['collateralType']
        ) {
          init[this.hubData['collateralType'].parentGroup].collateralType =
            Number(val);
          this.applyLoanType(init, val);
        }
        return init;
      }, {})
      .thru(loanData => {
        loanData.borrower['coBorrower'] = loanData.coBorrower;
        delete loanData.coBorrower;
        _.each(loanProducts, product => {
          loanData[product.shortName] = {
            selected: true,
            perPayment: '',
            daily: ''
          };
        });
        return loanData;
      })
      .value();
  }

  /**
   * Decides Loan Type
   * @param init {any}
   * @param val {any}
   */
  applyLoanType(init: any, val: any): void {
    if (Number(val) === Number(defaultOtherCollateralType)) {
      init[this.hubData['LoanType'].parentGroup].loanType =
        LoanType.OtherCollateral;
    } else if (Number(val) === Number(defaultCollateralType)) {
      init[this.hubData['LoanType'].parentGroup].loanType = LoanType.Vehicle;
    }
  }

  /**
   * Method to process hub data keys and construct the hubData Obj
   * @param init any
   * @param val any
   * @param key string
   */
  processKeys(init: any, val: any, key: string): void {
    if (
      this.hubData[key]?.keyName === 'totalAmountFinanced' &&
      !init[this.hubData[key].parentGroup].amountFinanced
    ) {
      init[this.hubData[key].parentGroup].amountFinanced = Number(val);
    }
    if (this.hubData[key]?.keyName === 'otherFees') {
      init[this.hubData[key].parentGroup].otherFees = Number(val);
    }
    if (this.hubData[key]?.keyName === 'isVehicleNotFound') {
      init[this.hubData[key].parentGroup].isVehicleNotFound = !!val;
    }

    if (this.hubData[key]?.keyName === 'deductions') {
      init[this.hubData[key].parentGroup].deductions = Number(val);
    }

    if (this.hubData[key]?.keyName === 'loanToValue') {
      init[this.hubData[key].parentGroup].loanToValue = Number(val);
    }

    if (this.hubData[key]?.keyName === 'referralCode') {
      init[this.hubData[key].parentGroup].referralCode = val;
    }
    if (
      [
        'insPolicyNumber',
        'insName',
        'insAgent',
        'insAgentPhone',
        'vin'
      ].includes(this.hubData[key]?.keyName)
    ) {
      init[this.hubData[key].parentGroup][this.hubData[key]?.keyName] = val
        ? val?.toString()
        : '';
    }

    if (this.hubData[key]?.keyName === 'interestRate') {
      init[this.hubData[key].parentGroup].interestRate =
        Number((+val).toFixed(6)) || 0;
    }

    if (this.hubData[key]?.keyName === 'termInMonths') {
      init[this.hubData[key].parentGroup].termInMonths = val || 60;
    }
  }

  /**
   * Checks if a keyName from hubData is in the list of dateFieldsInProposal list
   * @param key string
   * @returns boolean
   */
  isDateField(key: string): boolean {
    return _.includes(this.dateFieldsInProposal, this.hubData[key]?.keyName);
  }

  /**
   * Method to check date fields and convert them to ISOString
   * @param key string
   * @param val any
   * @param hubData any
   */
  checkDateFields(init: any, val: any, key: string, hubData: any): void {
    if (!this.isDateField(key)) return;

    const { parentGroup, keyName } = this.hubData[key];

    const newVal = this.setLoanAndPaymentDates(val, key, hubData);

    const isDobInvalid = keyName === 'dateOfBirth' && !newVal;
    init[parentGroup][keyName] = isDobInvalid
      ? ''
      : moment(newVal)?.format('YYYY-MM-DD');
  }

  /**
   * Sets ISOstring format dates to LoanDate and FirstPaymentDate
   * @param val any
   * @param key string
   * @param hubData any
   * @returns string
   */
  setLoanAndPaymentDates(val: any, key: string, hubData: any): string {
    if (_.includes(['LoanDate', 'FirstPaymentDate'], key) && !val) {
      const dateToBeAdded = new Date();
      dateToBeAdded.setDate(dateToBeAdded.getDate() + 15);
      if (key === 'LoanDate') {
        return !hubData.LoanDate && hubData.LosId
          ? val
          : new Date().toISOString();
      } else {
        return dateToBeAdded.toISOString();
      }
    }
    return val;
  }

  /**
   * Use this method to write data back to local hubData so it can be retrieved later
   * @param loan or proposal object {IProposal}
   */
  updateHubData({ borrower, loanData, vehicle }: IProposal): void {
    const localHubData = JSON.parse(sessionStorage.getItem('hubData'));
    if (borrower) {
      localHubData['AcctNum'] = borrower.accountNumber;
      localHubData['FirstName'] = borrower.firstName;
      localHubData['LastName'] = borrower.lastName;
      localHubData['Address1'] = borrower.address1;
      localHubData['Address2'] = borrower.address2;
      localHubData['City'] = borrower.city;
      localHubData['State'] = borrower.state
        ? borrower.state.toUpperCase()
        : borrower.state;
      localHubData['Phone'] = borrower.phoneNumber;
      localHubData['EmailAddress'] = borrower.emailAddress;
      localHubData['Zip'] = borrower.zipCode;
      localHubData['DateOfBirth'] = moment(borrower.dateOfBirth)?.format(
        'YYYY-MM-DD'
      );
      localHubData['COBFirstName'] = borrower.coBorrower?.firstName;
      localHubData['COBLastName'] = borrower.coBorrower?.lastName;
      localHubData['CoBorrowerDOB'] = borrower.coBorrower?.dateOfBirth;
    }
    if (loanData) {
      localHubData['LoanNum'] = loanData.loanNumber;
      localHubData['OtherFees'] = loanData.otherFees;
      localHubData['DownPayment'] = loanData.deductions;
      localHubData['PaymentsPerYear'] = loanData.paymentsPerYear;
      localHubData['LoanDate'] = moment(loanData.loanDate)?.format(
        'YYYY-MM-DD'
      );
      localHubData['FirstPaymentDate'] = moment(
        loanData.firstPaymentDate
      )?.format('YYYY-MM-DD');
      localHubData['PurchasePrice'] = loanData.amountFinanced;
      localHubData['Rate'] = loanData.interestRate;
      localHubData['insName'] = loanData.insName;
      localHubData['insPolicyNumber'] = loanData.insPolicyNumber;
      localHubData['insAgent'] = loanData.insAgent;
      localHubData['insAgentPhone'] = loanData.insAgentPhone;
      localHubData['Term'] = loanData.totalMonths || loanData.termInMonths;
      localHubData['loanToValue'] = loanData.loanToValue;
    }
    if (vehicle) {
      localHubData['VIN'] = vehicle.vin
        ? vehicle.vin.toUpperCase()
        : vehicle.vin;
      localHubData['VehicleYear'] = vehicle.year;
      localHubData['VehicleMake'] = vehicle.make;
      localHubData['VehicleModel'] = vehicle.model;
      localHubData['VehicleStyle'] = vehicle.style;
      localHubData['VehicleMakeCode'] = vehicle.makeCode;
      localHubData['VehicleModelCode'] = vehicle.modelCode;
      localHubData['VehicleStyleCode'] = vehicle.styleCode;
      localHubData['Odometer'] = vehicle.odometer;
      localHubData['MockVin'] = vehicle.mockVin;
      localHubData['collateralType'] = vehicle['collateralType'];
      localHubData['collateralCode'] = vehicle['collateralCode'];
      localHubData['VehiclePurchasePrice'] = vehicle['purchasePrice'] || 0;
      localHubData['RetailPrice'] = vehicle['retailPrice'] || 0;
      localHubData['IsVehicleNotFound'] = !!vehicle.isVehicleNotFound;
      localHubData['ValidateVin'] = !!vehicle['validateVin'];
    }
    sessionStorage.setItem('hubData', JSON.stringify(localHubData));
  }

  /**
   * Method to update borrower object in hubData
   * @param borrower {IBorrower}
   */
  updateHubBorrowerData(borrower: IBorrower): void {
    const localHubData = JSON.parse(sessionStorage.getItem('hubData'));
    localHubData['AcctNum'] = borrower.accountNumber;
    localHubData['FirstName'] = borrower.firstName;
    localHubData['LastName'] = borrower.lastName;
    localHubData['Address1'] = borrower.address1;
    localHubData['Address2'] = borrower.address2;
    localHubData['City'] = borrower.city;
    localHubData['State'] = borrower.state
      ? borrower.state.toUpperCase()
      : borrower.state;
    localHubData['Phone'] = borrower.phoneNumber;
    localHubData['EmailAddress'] = borrower.emailAddress;
    localHubData['Zip'] = borrower.zipCode;
    localHubData['DateOfBirth'] = moment(borrower.dateOfBirth)?.format(
      'YYYY-MM-DD'
    );
    localHubData['COBFirstName'] = borrower.coBorrower?.firstName;
    localHubData['COBLastName'] = borrower.coBorrower?.lastName;
    localHubData['CoBorrowerDOB'] = borrower.coBorrower?.dateOfBirth;
    sessionStorage.setItem('hubData', JSON.stringify(localHubData));
  }

  updateHubVehicleData(vehicle: Partial<IVehicle>): void {
    const localHubData = JSON.parse(sessionStorage.getItem('hubData') || '{}');
    if (vehicle) {
      localHubData['VIN'] = vehicle.vin
        ? vehicle.vin.toUpperCase()
        : vehicle.vin;
      localHubData['VehicleYear'] = vehicle.year;
      localHubData['VehicleMake'] = vehicle.make;
      localHubData['VehicleModel'] = vehicle.model;
      localHubData['VehicleStyle'] = vehicle.style;
      localHubData['VehicleMakeCode'] = vehicle.makeCode;
      localHubData['VehicleModelCode'] = vehicle.modelCode;
      localHubData['VehicleStyleCode'] = vehicle.styleCode;
      localHubData['Odometer'] = vehicle.odometer;
      localHubData['MockVin'] = vehicle.mockVin;
      localHubData['collateralType'] = vehicle['collateralType'];
      localHubData['collateralCode'] = vehicle['collateralCode'];
      localHubData['VehiclePurchasePrice'] = vehicle['purchasePrice'] || 0;
      localHubData['RetailPrice'] = vehicle['retailPrice'] || 0;
      localHubData['IsVehicleNotFound'] = !!vehicle.isVehicleNotFound;
      localHubData['ValidateVin'] = !!vehicle['validateVin'];
      sessionStorage.setItem('hubData', JSON.stringify(localHubData));
    }
  }

  /**
   * Check if Express is just launched with LosId (retrieve quote for Meridian Link with just username/password/losId)
   * @param localHubData {{key: string, value: string | number | null}}
   */
  checkEmptyHubData(localHubData: {
    key: string;
    value: string | number | null;
  }): boolean {
    let isEmpty: boolean = true;
    // List of Hub Props to check against to make sure they have no data
    const localHubItemsToCheck: string[] = [
      'AcctNum',
      'FirstName',
      'LastName',
      'Address1',
      'Address2',
      'City',
      'State',
      'Zip',
      'Phone',
      'EmailAddress',
      'DateOfBirth',
      'VehicleYear',
      'VIN',
      'Odometer',
      'VehicleMake',
      'VehicleModel',
      'VehicleStyle',
      'PurchasePrice',
      'DownPayment',
      'LoanDate',
      'FirstPaymentDate',
      'LoanNum',
      'Rate',
      'Term',
      'OtherFees'
    ];
    for (const prop of Object.keys(localHubData)) {
      if (
        localHubItemsToCheck.includes(prop) &&
        localHubData[prop] !== 0 &&
        localHubData[prop] !== null &&
        localHubData[prop] !== undefined &&
        localHubData[prop] !== ''
      ) {
        isEmpty = false;
      }
    }
    return isEmpty;
  }

  /**
   * Takes a loan object, hubdata and updates the borrower, loanData, vehicle fields from the latest Hub Data (in case of user loading a proposal with updated information)
   * @param proposalData {IProposal}
   * @param hubData {IProposal}
   */
  updateProposalData(proposalData: IProposal, hubData: IProposal): void {
    const dateFormat = 'YYYY-MM-DD';
    const dateFormat2 = 'YYYY-MM-DDTHH:mm:ss';
    const productsArr = Object.values(eProductNames);
    for (const key of Object.keys(proposalData)) {
      this.isDateformatValid(
        dateFormat,
        dateFormat2,
        key,
        proposalData,
        hubData
      );
      if (
        !productsArr.includes(key as eProductNames) &&
        hubData &&
        !!(key in hubData) &&
        proposalData[key] !== null &&
        proposalData[key] !== undefined
      ) {
        if (typeof proposalData[key] == 'object') {
          this.updateProposalData(proposalData[key], hubData[key]);
        } else if (
          proposalData[key] != hubData[key] &&
          hubData[key] !== '' &&
          hubData[key] !== null &&
          hubData[key] !== undefined
        ) {
          proposalData[key] = hubData[key]; // Only update proposal data if hubData has a valid value in it.
        } else if (
          proposalData[key] &&
          proposalData[key] != hubData[key] &&
          (hubData[key] === '' ||
            hubData[key] === null ||
            hubData[key] === undefined)
        ) {
          this.updateHubData(proposalData);
        }
      }
    }
  }

  /**
   * Checks if Loan date and First payment dates are valid
   * @param dateFormat {string}
   * @param dateFormat2 {string}
   * @param key {string}
   * @param proposalData {IProposal}
   * @param hubData {IProposal}
   */
  isDateformatValid(
    dateFormat: string,
    dateFormat2: string,
    key: string,
    proposalData: IProposal,
    hubData: IProposal
  ): void {
    if (
      typeof proposalData[key] !== 'object' &&
      (moment(proposalData[key], dateFormat, true).isValid() ||
        moment(proposalData[key], dateFormat2, true).isValid())
    ) {
      proposalData[key] = moment(proposalData[key]).format('YYYY-MM-DD');
      if (
        (key === 'firstPaymentDate' || key === 'loanDate') &&
        sessionStorage.getItem('isLoanDateAutoConfigured') === 'true'
      ) {
        hubData[key] = proposalData[key];
      }
    }
  }
}
